/**
 * 升學年提示 banner(boss 2026-06-10:「等這次期末考結束就換成四年級課程」)。
 *
 * 學習堡壘首頁一條一次性提示:當「目前學年的期末考日期已過」且尚未 dismiss 過該學年,
 * 顯示「這學期結束了,要升上四年級嗎?(舊單元會留在『複習』)」:
 *   - 「升上 N」→ `advanceGradeSemester()`(只切 `currentGradeSemester`,不碰戰力 /
 *     帳號)+ 標記該學年已 dismiss → banner 收起,不再煩。
 *   - 「之後再說」→ 只標記該學年已 dismiss → banner 收起。
 *
 * 觸發判斷走 `grade-semester.ts` 的純函式 `shouldShowGradeUpgradeBanner`(已單測)。
 * 期末考日期來源 = exam-planner 的 `loadExamPlan()`(已存在);無 final 事件 ⇒ 不顯示
 * (此時走設定頁手動升級鈕)。視覺 = 學術風格(紙底提示條,專業介面、無彩光)。
 */

import { loadProgress, saveProgress } from '../shared/progress';
import { loadExamPlan } from './exam-plan/store';
import {
  gradeSemesterOf,
  gradeSemesterLabel,
  nextGradeSemester,
  shouldShowGradeUpgradeBanner,
  advanceGradeSemester,
} from './grade-semester';
import type { GradeSemester } from '../shared/types';

/** 把某學年加入 `dismissedGradeUpgrade`(去重)並持久化 —— 一次性提示用。 */
function markGradeUpgradeDismissed(grade: GradeSemester): void {
  const p = loadProgress();
  if (!p) return;
  const prev = p.dismissedGradeUpgrade ?? [];
  if (prev.includes(grade)) return;
  p.dismissedGradeUpgrade = [...prev, grade];
  saveProgress(p);
}

/**
 * Mount 升學年 banner 到 `container`。不該顯示時 = no-op(不插任何節點),回傳 unmount。
 *
 * 「該不該顯示」全交給 `shouldShowGradeUpgradeBanner` 純函式判斷(目前學年未封頂 +
 * 期末考已過 + 該學年未 dismiss)。本函式只負責讀存檔組參數、畫 DOM、接事件。
 *
 * @param container 掛載點(學習堡壘首頁的 banner slot)。
 * @param onUpgraded 升學年後回呼(呼叫端可重繪首頁讓範圍清單即時反映)。
 */
export function renderGradeUpgradeBanner(
  container: HTMLElement,
  onUpgraded?: (next: GradeSemester) => void,
): () => void {
  const progress = loadProgress();
  const current = gradeSemesterOf(progress);
  const dismissed = progress?.dismissedGradeUpgrade ?? [];
  const examPlan = loadExamPlan();

  const root = document.createElement('div');
  root.className = 'grade-upgrade-banner';
  root.setAttribute('data-testid', 'grade-upgrade-banner');

  function destroy(): void {
    root.remove();
  }

  if (!shouldShowGradeUpgradeBanner(current, examPlan, undefined, dismissed)) {
    // no-op:不顯示(不插節點)。回傳 unmount 仍安全(root 未掛載)。
    return destroy;
  }

  const next = nextGradeSemester(current);
  root.style.cssText =
    'display:flex;align-items:center;gap:12px;padding:12px 14px;margin:10px 0;' +
    'background:var(--ef-note);border:1px solid var(--ef-line);border-radius:var(--ef-radius-sm);';
  root.innerHTML = `
    <span aria-hidden="true" style="font-size:1.6em;line-height:1;">🎓</span>
    <div style="flex:1;min-width:0;">
      <div style="font-weight:700;color:var(--ef-ink);font-size:15px;">這學期結束了,要升上${gradeSemesterLabel(next)}嗎?</div>
      <div style="font-size:12px;color:var(--ef-sub);margin-top:2px;line-height:1.4;">舊單元會留在「複習」,隨時可以複習。</div>
    </div>
    <div style="display:flex;gap:8px;flex-wrap:wrap;">
      <button class="ef-btn ef-btn--secondary" data-testid="grade-upgrade-later"
        style="white-space:nowrap;">之後再說</button>
      <button class="ef-btn ef-btn--primary" data-testid="grade-upgrade-confirm"
        style="white-space:nowrap;">升上${gradeSemesterLabel(next)}</button>
    </div>
  `;

  const laterBtn = root.querySelector('[data-testid="grade-upgrade-later"]') as HTMLButtonElement;
  const confirmBtn = root.querySelector('[data-testid="grade-upgrade-confirm"]') as HTMLButtonElement;

  laterBtn.addEventListener('click', () => {
    markGradeUpgradeDismissed(current);
    destroy();
  });
  confirmBtn.addEventListener('click', () => {
    const upgraded = advanceGradeSemester();
    // 同時把「原學年」標 dismiss,避免升級後若又被設回原學年時重複彈。
    markGradeUpgradeDismissed(current);
    destroy();
    onUpgraded?.(upgraded);
  });

  container.appendChild(root);
  return destroy;
}
